import {Spin, Typography} from "antd"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import {AuthorizeNetRecurringSubscriptionForm} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/AuthorizeNetRecurringSubscriptionForm"
import {DirectEmbedRecurringSubscriptionForm} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/DirectEmbedRecurringSubscriptionForm"
import {RetailersGatewayRecurringSubscriptionForm} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/RetailersGatewayRecurringSubscriptionForm"
import {PaypalRecurringSubscriptionForm} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/PaypalRecurringSubscriptionForm"
import {
    getRecurringPaymentFromPaymentPlan,
    RecurringPaymentFromPaymentPlan
} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/RecurringPaymentPopup"
import StripeRecurringPaymentForm from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/StripeRecurringPaymentForm"
import {PaymentPlan} from "types/student-account/payment-plan"
import {PaymentProvider, PreparePaypalRecurringSubscriptionApiResponse} from "types/student-account/student-payment"

type Props = {
    profileId: number
    paymentPlan: Partial<PaymentPlan>
    // The provider PaymentView already resolved for this profile — render based on this, not on
    // independently re-derived global enabled-flags, so this always matches what the user is
    // actually paying with even if multiple providers are configured for the same profile.
    providerConfig: any
    onSuccess: () => void
    isPreparing?: boolean
    paypalPrepare?: PreparePaypalRecurringSubscriptionApiResponse | null
}

export const RecurringPaymentCheckout = ({
    profileId,
    paymentPlan,
    providerConfig,
    onSuccess,
    isPreparing = false,
    paypalPrepare = null
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [loadKey] = useState(() => Math.random())

    const recurring: RecurringPaymentFromPaymentPlan = useMemo(
        () => getRecurringPaymentFromPaymentPlan(paymentPlan),
        [paymentPlan]
    )

    const amountInCents = Math.round((recurring.amount || 0) * 100)
    const checkoutWithStripe = providerConfig?.provider === PaymentProvider.Stripe
    const checkoutWithPaypal = providerConfig?.provider === PaymentProvider.Paypal
    const checkoutWithAuthorizeNet = providerConfig?.provider === PaymentProvider.AuthorizeNet
    const checkoutWithDirectEmbed = providerConfig?.provider === PaymentProvider.DirectEmbed
    const checkoutWithRetailersGateway = providerConfig?.provider === PaymentProvider.RetailersGateway

    if (isPreparing) {
        return <Spin />
    }

    return (
        <div>
            {checkoutWithStripe && (
                <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                    <StripeRecurringPaymentForm
                        profileId={profileId}
                        onSuccess={onSuccess}
                        amount={recurring.amount}
                        currency="usd"
                        interval={recurring.interval}
                        intervalCount={recurring.intervalCount}
                        cancelAt={null}
                        paymentPlanItems={recurring.paymentPlanItems}
                        submitButtonTitle="PAY"
                        preferNewCard
                    />
                </StripeElementsProvider>
            )}

            {checkoutWithPaypal && (
                <>
                    {providerConfig?.clientId ? (
                        <PaypalRecurringSubscriptionForm
                            key={loadKey}
                            profileId={profileId}
                            clientId={providerConfig.clientId}
                            amountInCents={amountInCents}
                            currency="usd"
                            interval={recurring.interval}
                            intervalCount={recurring.intervalCount}
                            cancelAt={null}
                            paymentPlanItems={recurring.paymentPlanItems}
                            paymentPlanId={paymentPlan.paymentPlanId}
                            existingPrepare={
                                paypalPrepare?.paypalPlanId && paypalPrepare?.recurringPaymentId
                                    ? {
                                          recurringPaymentId: paypalPrepare.recurringPaymentId,
                                          paypalPlanId: paypalPrepare.paypalPlanId
                                      }
                                    : undefined
                            }
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <Typography.Paragraph type="danger">
                            {t("msg.recurringPayment.paypalMissingClientConfig")}
                        </Typography.Paragraph>
                    )}
                </>
            )}

            {checkoutWithAuthorizeNet && (
                <AuthorizeNetRecurringSubscriptionForm
                    key={loadKey}
                    profileId={profileId}
                    amountInCents={amountInCents}
                    currency="usd"
                    interval={recurring.interval}
                    intervalCount={recurring.intervalCount}
                    cancelAt={null}
                    paymentPlanItems={recurring.paymentPlanItems}
                    paymentPlanId={paymentPlan.paymentPlanId}
                    onSuccess={onSuccess}
                />
            )}

            {checkoutWithDirectEmbed && (
                <DirectEmbedRecurringSubscriptionForm
                    key={loadKey}
                    profileId={profileId}
                    amountInCents={amountInCents}
                    currency="usd"
                    interval={recurring.interval}
                    intervalCount={recurring.intervalCount}
                    cancelAt={null}
                    paymentPlanItems={recurring.paymentPlanItems}
                    paymentPlanId={paymentPlan.paymentPlanId}
                    onSuccess={onSuccess}
                />
            )}

            {checkoutWithRetailersGateway && (
                <RetailersGatewayRecurringSubscriptionForm
                    key={loadKey}
                    profileId={profileId}
                    amountInCents={amountInCents}
                    currency="usd"
                    interval={recurring.interval}
                    intervalCount={recurring.intervalCount}
                    cancelAt={null}
                    paymentPlanItems={recurring.paymentPlanItems}
                    paymentPlanId={paymentPlan.paymentPlanId}
                    onSuccess={onSuccess}
                />
            )}
        </div>
    )
}
